import {
  SingleGroupMetricTitle,
  SingleGroupMetricValue,
} from "./SingleGroupMetric";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { TooLongForDataDumpStyles } from "./GroupMetricsStyles";
import styled, {
  css,
  FlattenSimpleInterpolation,
  createGlobalStyle,
} from "styled-components/macro";
import {
  FONT_SIZE_EXTRA_SMALL,
  Z_INDEX,
  BREAKPOINT_TABLET,
  BREAKPOINT_MOBILE,
} from "constants/constants";
import { darkGrey, darkModeTextColor } from "constants/colors";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { Button, TextField, Tooltip } from "@mui/material";
import {
  getShowTooltip,
  GROUP_METRIC_MARGIN_HZ,
  GROUP_METRIC_WIDTH,
} from "./groupMetricsUtils";
import {
  useIsAdmin,
  useIsSingleProcessPage,
  usePnlPopupTimeRange,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useShouldUseDataDumpsForGroupMetrics } from "utils/customHooks/whichServerHooks";
import { useIsDarkMode } from "store/atoms";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { CUSTOMERS_WHO_SHOW_EDITABLE_METRIC } from "constants/customers";
import { DataDumpAggregateType, Process } from "types/types";
import { useCallback, useMemo, useState, useTransition } from "react";
import DataDumpGetter from "segments/DataDumpGetter/DataDumpGetter";
import { HtmlTooltip } from "./HtmlTooltip";
import { ExplainerTooltipContent } from "../ExplainerTooltip";

const GroupMetricStyles = styled.div<{
  $customCss?: string | FlattenSimpleInterpolation;
  $isPreferred?: boolean;
  $isDarkMode?: boolean;
}>`
  margin: 0 ${GROUP_METRIC_MARGIN_HZ}px;
  z-index: ${Z_INDEX.L1};
  transition: transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
  transform-origin: center;
  transform: scale(${props => (props.$isPreferred ? 1.2 : 1) * 0.8});
  width: ${GROUP_METRIC_WIDTH.sm}px;
  @media (min-width: ${BREAKPOINT_MOBILE}px) {
    width: ${GROUP_METRIC_WIDTH.md}px;
  }
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    width: ${GROUP_METRIC_WIDTH.lg}px;
    transform: scale(${props => (props.$isPreferred ? 1.2 : 1)});
  }
  .MuiButton-root {
    height: 100%;
    width: 100%;
    text-transform: none;
    padding: 8px !important;
    margin: -8px !important;
    box-sizing: content-box;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
  }
  .metricTitle {
    color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : darkGrey)};
    font-size: ${FONT_SIZE_EXTRA_SMALL}px;
    font-weight: ${props => (props.$isPreferred ? "bold" : "normal")};
  }
  ${({ $customCss }) => $customCss || ""}
`;

export const GroupMetric = ({
  showTooltip,
  isTooLongForDataDump = false,
  units,
  value,
  displayName,
  isPnlPopup = false,
  description,
  dataDumpKey,
  className = "",
  decimalPoints,
  formatHoursMinutes,
  overrideMachemetryIds,
  allProcesses,
  shouldFetchOverriddenMetric,
}: {
  showTooltip: boolean;
  isTooLongForDataDump?: boolean;
  units?: string;
  value?: number | null;
  displayName: string;
  isPnlPopup?: boolean;
  description?: string;
  dataDumpKey?: keyof DataDumpAggregateType;
  className?: string;
  decimalPoints?: number;
  formatHoursMinutes?: boolean;
  overrideMachemetryIds?: string[];
  allProcesses: Process[];
  shouldFetchOverriddenMetric?: boolean;
}) => {
  const { preferredMetric } = useSettings();
  const isPreferred = preferredMetric === dataDumpKey;

  const { translate } = useFlightTranslation();
  const shouldUseDataDumps = useShouldUseDataDumpsForGroupMetrics();
  const isSingleProcessPage = useIsSingleProcessPage();
  const [isDarkMode] = useIsDarkMode();

  const overriddenTooltipText = useMemo(() => {
    const overriddenProcessNames =
      overrideMachemetryIds?.map(
        id => allProcesses.find(p => p.id === id)?.name ?? id
      ) ?? [];
    const overriddenTooltipText =
      overrideMachemetryIds && overrideMachemetryIds.length > 0
        ? `* only for ${overriddenProcessNames?.join(", ")}`
        : "";
    return overriddenTooltipText;
  }, [allProcesses, overrideMachemetryIds]);
  const tooltipTitle = useMemo(() => {
    return (
      (isPreferred ? translate("Your_Preferred_Metric") : "") +
      overriddenTooltipText
    );
  }, [isPreferred, overriddenTooltipText, translate]);

  const [overriddenMetricValue, setOverriddenMetricValue] = useState(null);

  const valueDisplay = shouldFetchOverriddenMetric
    ? overriddenMetricValue
    : value;

  return (
    <>
      {shouldFetchOverriddenMetric ? (
        <OverriddenMetricGetter
          {...{ setOverriddenMetricValue, overrideMachemetryIds, dataDumpKey }}
        />
      ) : null}
      <Tooltip title={tooltipTitle} placement={"top"}>
        <GroupMetricStyles
          {...{ $isPreferred: isPreferred, $isDarkMode: isDarkMode }}
          className={`${className} metricDiv metric-${dataDumpKey}${isPreferred ? " preferred" : ""}`}
          key={displayName}
          data-server-names={[
            shouldUseDataDumps
              ? isSingleProcessPage
                ? SERVER_NAMES.midgardDataDumpsFullRangeByProcess
                : SERVER_NAMES.midgardDataDumpFullRange
              : SERVER_NAMES.askrAggregates,
          ]}
        >
          <GroupMetricButton
            {...{
              dataDumpKey,
              isPnlPopup,
              displayName,
              description,
              overriddenTooltipText,
              isTooLongForDataDump,
              value: valueDisplay,
              units,
              decimalPoints,
              formatHoursMinutes,
              allProcesses,
            }}
          />
        </GroupMetricStyles>
      </Tooltip>
    </>
  );
};

function OverriddenMetricGetter({
  setOverriddenMetricValue,
  overrideMachemetryIds,
  dataDumpKey,
}: {
  setOverriddenMetricValue: React.Dispatch<React.SetStateAction<any> | null>;
  overrideMachemetryIds;
  dataDumpKey?: keyof DataDumpAggregateType;
}) {
  const pnlPopupTimeRange = usePnlPopupTimeRange();
  return (
    <DataDumpGetter
      processIds={overrideMachemetryIds}
      // * dataDumpAggType unused since we have customOnCompleted
      dataDumpAggType="UPDATE_DATA_DUMP_TREND_CHART_FOR_PROCESSES"
      timeRange={pnlPopupTimeRange}
      customOnCompleted={data => {
        if (data.dataDumpAggregate && dataDumpKey) {
          setOverriddenMetricValue(data.dataDumpAggregate[dataDumpKey]);
        }
      }}
    />
  );
}

function GroupMetricButton({
  dataDumpKey,
  isPnlPopup,
  displayName,
  description,
  overriddenTooltipText,
  isTooLongForDataDump,
  value,
  units,
  decimalPoints,
  formatHoursMinutes,
  allProcesses,
}: {
  dataDumpKey?: keyof DataDumpAggregateType;
  isPnlPopup: boolean;
  displayName: string;
  description?: string;
  overriddenTooltipText?: string;
  isTooLongForDataDump?: boolean;
  value?: number | null;
  units?: string;
  decimalPoints?: number;
  formatHoursMinutes?: boolean;
  allProcesses: Process[];
}) {
  const { preferredMetric, setPreferredMetric } = useSettings();
  const isPreferred = useMemo(
    () => preferredMetric === dataDumpKey,
    [preferredMetric, dataDumpKey]
  );
  const [isPending, startTransition] = useTransition();
  const shouldUseDataDumps = useShouldUseDataDumpsForGroupMetrics();
  const isAdminUser = useIsAdmin();

  const disabled = useMemo(
    () => isPreferred || isPnlPopup || isPending,
    [isPreferred, isPnlPopup, isPending]
  );

  const tooltipTitle = useMemo(
    () =>
      isAdminUser && !disabled
        ? `${shouldUseDataDumps ? "midgard:dataDumpAggregate" : "askr:productionAggregates"}:${dataDumpKey}`
        : "",
    [isAdminUser, disabled, shouldUseDataDumps, dataDumpKey]
  );
  const handleClick = useCallback(() => {
    if (!disabled && dataDumpKey) {
      startTransition(() => {
        setPreferredMetric(dataDumpKey);
      });
    }
  }, [disabled, dataDumpKey, startTransition, setPreferredMetric]);
  const content = (
    <Button
      className="btnGroupMetrics"
      onClick={handleClick}
      style={{ cursor: disabled ? "default" : "pointer" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
          pointerEvents: "none",
        }}
      >
        <SingleGroupMetricTitle
          {...{
            displayName,
            allProcesses,
          }}
        />
        {isTooLongForDataDump && displayName === "Output" ? (
          <TooLongForDataDumpStyles className="metricValue">
            Not yet available for long ranges
          </TooLongForDataDumpStyles>
        ) : (
          <SingleGroupMetricValue
            {...{
              value,
              units,
              isPnlPopup,
              isPreferred,
              decimalPoints,
              formatHoursMinutes,
            }}
          />
        )}
      </div>
    </Button>
  );
  const ckey = useCustomerKey();
  const showTooltip = useMemo(
    () =>
      getShowTooltip({
        ckey,
        dataDumpKey,
        description,
      }),
    [ckey, dataDumpKey, description]
  );
  return (
    <Tooltip title={tooltipTitle} placement={"top"}>
      {showTooltip ? (
        <>
          <MetricTooltipStyles />
          <HtmlTooltip
            openOnMouseover={true}
            style={{
              width: "100%",
              height: "fit-content",
              zIndex: Z_INDEX.L8,
            }}
            PopperProps={{
              style: { pointerEvents: "none" },
              modifiers: [
                {
                  name: "flip",
                  enabled: false,
                },
              ],
            }}
            className={`btnHelpTooltip`}
            title={
              <ExplainerTooltipContent
                {...{ displayName, dataDumpKey, description }}
              />
            }
          >
            {content}
          </HtmlTooltip>
        </>
      ) : (
        content
      )}
    </Tooltip>
  );
}

const customTargetAtom = atomWithStorage<number>("customTarget", 100);
const useCustomTarget = () => useAtom(customTargetAtom);
/** a simple input box to track a target value */
export function CustomInputGroupMetric() {
  const [customTarget, setCustomTarget] = useCustomTarget();
  const ckey = useCustomerKey();
  return (
    <GroupMetricStyles
      $customCss={css`
        display: flex;
        flex-direction: column;
        .metricTitle {
          justify-content: center;
        }
        .MuiTextField-root {
          flex-grow: 1;
          .MuiInputBase-root {
            flex-grow: 1;
          }
          input {
            text-align: center;
            padding: 0 8px;
            box-sizing: border-box;
            width: 100%;
            height: 100%;
            font-size: 24px;
          }
        }
        /* hide the spin buttons unless we're hovering over/clicked into it */
        &:not(:hover, :focus-within) {
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
        }
      `}
    >
      <SingleGroupMetricTitle
        {...{
          displayName: CUSTOMERS_WHO_SHOW_EDITABLE_METRIC[ckey],
          metricLabel: null,
          description: "",
          allProcesses: [],
        }}
      />
      <TextField
        type="number"
        value={customTarget}
        onChange={event => {
          setCustomTarget(Number(event.target.value));
        }}
      />
    </GroupMetricStyles>
  );
}

const MetricTooltipStyles = createGlobalStyle`
  .btnHelpTooltip {
    pointer-events: all;
    .metricTitle, [class*=MetricValueStyles] {
      pointer-events: none;
    }
  }
`;
